import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Icone } from '../components/Icone';
import {
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { useToast } from '../components/ui/useToast';
import { appelApi, ErreurApi } from '../lib/api';
import { urlFichierExport } from '../lib/useExport';
import { useAuth } from '../auth/useAuth';
import { TOUS_LES_SITES } from '../site/context';
import { usePerimetre } from '../site/usePerimetre';
import { useMarque } from '../marque/useMarque';

interface SiteDetail {
  code: string;
  nom: string;
  ville: string | null;
  apprenants: number;
  entreprises: number;
  sessions: number;
  formationsEngagees: number;
  modes: { mode: 'CFA' | 'OF'; dossiers: number }[];
  equipe: number;
  assistantPedagogique: string | null;
  formateurs: number;
}

interface ReponseSites {
  sites: SiteDetail[];
  compteurs: {
    sitesActifs: number;
    apprenants: number;
    entreprises: number;
    assistants: number;
  };
  rattachements: string[];
}

/**
 * Écran « Gestion multi-sites » (CDC §2.3).
 *
 * Les effectifs sont **comptés**, pas estimés : un tableau multi-sites dont les
 * chiffres seraient approchés ne servirait à rien. Le périmètre d'une antenne se
 * lit dans les dossiers qu'elle porte — CFA, OF ou les deux.
 */
export function SitesPage(): JSX.Element {
  const navigate = useNavigate();
  const { site, definirSite } = usePerimetre();
  const { marque } = useMarque();
  const { peutAcceder } = useAuth();
  const peutGererAntennes = peutAcceder('adm-antennes');
  const notifier = useToast();
  const { data, isLoading, error } = useQuery({
    queryKey: ['sites-detail'],
    queryFn: () => appelApi<ReponseSites>('/sites/detail'),
  });

  const antennes = data?.sites ?? [];
  const toutesLesAntennes = site === TOUS_LES_SITES;

  // Le périmètre s'applique ici, pas seulement dans la sidebar : un écran qui
  // annonce « filtrage par antenne » et affiche quand même tout ne filtre pas.
  // Le filtrage se fait sur un jeu déjà restreint par l'API au périmètre
  // autorisé — il ne peut que réduire, jamais révéler une antenne de plus.
  const retenues = toutesLesAntennes
    ? antennes
    : antennes.filter((antenne) => antenne.code === site);
  const masquees = antennes.length - retenues.length;

  // Les compteurs suivent le périmètre affiché : « dans votre périmètre » doit
  // rester vrai quand une antenne est sélectionnée.
  const compteurs = data
    ? {
        sitesActifs: retenues.length,
        apprenants: retenues.reduce((somme, antenne) => somme + antenne.apprenants, 0),
        entreprises: retenues.reduce((somme, antenne) => somme + antenne.entreprises, 0),
        assistants: retenues.filter((antenne) => antenne.assistantPedagogique !== null).length,
      }
    : undefined;

  /**
   * Un(e) assistant(e) pédagogique n'a qu'une antenne : le périmètre par défaut
   * vaut `TOUS`, mais l'API le résout en son unique site. Écrire « toutes les
   * antennes » à quelqu'un qui n'en a qu'une serait faux — c'est son antenne
   * qui est nommée. `data?.sites` est déjà la liste de ses sites accessibles.
   */
  const antenneUnique = antennes.length === 1 ? antennes[0] : undefined;
  const perimetreLibelle = toutesLesAntennes
    ? (antenneUnique?.code ?? 'toutes les antennes')
    : site;

  /**
   * L'export vit derrière l'écran `adm-export` : un profil pédagogique voit
   * cet écran sans y avoir droit — un(e) assistant(e) de site encore moins.
   * On ne lui propose pas un bouton qui le mènerait sur un 403 en dehors de
   * l'application.
   */
  const peutExporter = peutAcceder('adm-export');

  const exporter = (): void => {
    notifier(`Export du suivi apprenants — ${perimetreLibelle}.`);
    // Content-Disposition: attachment — la SPA n'est pas quittée.
    window.location.href = urlFichierExport(site, []);
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Gestion multi-sites"
        sousTitre="Vue transverse · rattachement et filtrage par antenne"
        actions={
          peutGererAntennes ? (
            <BoutonSecondaire onClick={() => navigate('/adm-antennes')}>
              Paramétrer les antennes (/adm-antennes)
            </BoutonSecondaire>
          ) : undefined
        }
      />

      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Antennes actives"
          valeur={compteurs?.sitesActifs ?? '—'}
          detail="dans votre périmètre"
          ton="navy"
        />
        <Compteur
          libelle="Dossiers apprenants"
          valeur={compteurs?.apprenants ?? '—'}
          detail="toutes antennes confondues"
          ton="mint"
        />
        <Compteur
          libelle="Entreprises rattachées"
          valeur={compteurs?.entreprises ?? '—'}
          detail="multi-rattachement possible"
          ton="warm"
        />
        <Compteur
          libelle="Assistant(e)s pédagogiques"
          valeur={compteurs?.assistants ?? '—'}
          detail="une par antenne"
          ton="blue"
        />
      </div>

      <Carte titre={`Antennes de ${marque.nom}`} sansBordure>
        {error && (
          <div className="p-4 sm:p-6">
            <MessageErreur
              message={
                error instanceof ErreurApi
                  ? error.message
                  : 'Les antennes n’ont pas pu être chargées.'
              }
            />
          </div>
        )}
        {isLoading && <Chargement libelle="Chargement des antennes…" />}

        {!isLoading && !error && (
          <div className="relative overflow-x-auto">
            <table className="w-full min-w-[700px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {[
                    'Antenne',
                    'Périmètre',
                    'Dossiers',
                    'Formations engagées',
                    'Assistant(e) pédagogique',
                    'Équipe',
                    '',
                  ].map((colonne, index) => (
                    <th
                      key={colonne || index}
                      className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                    >
                      {colonne || <span className="sr-only">Actions</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {retenues.map((antenne) => {
                  const courante = antenne.code === site;

                  return (
                    <tr
                      key={antenne.code}
                      className={`transition ${
                        courante && !toutesLesAntennes ? 'bg-codialis-sand' : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-semibold text-codialis-navy">{antenne.code}</div>
                        <div className="text-[11px] text-codialis-gray">
                          {antenne.nom}
                          {antenne.ville ? ` · ${antenne.ville}` : ''}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {antenne.modes.length === 0 ? (
                            <Pastille texte="Aucun dossier" ton="neutral" />
                          ) : (
                            antenne.modes.map((mode) => (
                              <Pastille
                                key={mode.mode}
                                texte={`${mode.mode} · ${mode.dossiers}`}
                                ton={mode.mode === 'CFA' ? 'info' : 'mint'}
                              />
                            ))
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-codialis-gray">
                        {antenne.apprenants} apprenant{antenne.apprenants > 1 ? 's' : ''}
                        <div className="text-[10px]">
                          {antenne.entreprises} entreprise{antenne.entreprises > 1 ? 's' : ''}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-codialis-gray">
                        {antenne.formationsEngagees}
                        <div className="text-[10px]">
                          {antenne.sessions} session{antenne.sessions > 1 ? 's' : ''} OF
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-codialis-gray">
                        {antenne.assistantPedagogique ?? (
                          <span className="text-amber-600">à affecter</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-codialis-gray">
                        {antenne.equipe} personne{antenne.equipe > 1 ? 's' : ''}
                        <div className="text-[10px]">
                          {antenne.formateurs} formateur{antenne.formateurs > 1 ? 's' : ''}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            definirSite(courante ? TOUS_LES_SITES : antenne.code)
                          }
                          aria-pressed={courante}
                          className={`inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition ${
                            courante
                              ? 'bg-codialis-navy text-white hover:bg-codialis-deep'
                              : 'text-codialis-sky hover:bg-gray-100'
                          }`}
                        >
                          <Icone nom={courante ? 'croix' : 'filtre'} className="h-3.5 w-3.5" />
                          {courante ? 'Retirer le filtre' : 'Filtrer'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {masquees > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-codialis-sand/60 px-4 py-3 text-xs text-codialis-gray">
                <span>
                  Périmètre restreint à l’antenne {site} · {masquees} antenne
                  {masquees > 1 ? 's' : ''} masquée{masquees > 1 ? 's' : ''}.
                </span>
                <button
                  type="button"
                  onClick={() => definirSite(TOUS_LES_SITES)}
                  className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] items-center gap-1.5 rounded-lg px-3 py-2 font-semibold text-codialis-sky transition hover:bg-white"
                >
                  <Icone nom="croix" className="h-3.5 w-3.5" />
                  Voir toutes les antennes
                </button>
              </div>
            )}

            {retenues.length === 0 && (
              <p className="px-4 py-6 text-sm text-codialis-gray">
                Aucune antenne dans le périmètre sélectionné.
              </p>
            )}
          </div>
        )}
      </Carte>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Carte titre="Ce qui est rattaché à une antenne" sansBordure>
          <div className="space-y-2 p-4 sm:p-6 text-sm">
            {(data?.rattachements ?? []).map((rattachement) => (
              <div key={rattachement} className="flex items-start gap-2 text-codialis-gray">
                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-codialis-sky" />
                {rattachement}
              </div>
            ))}
            <p className="pt-2 text-[11px] text-codialis-gray">
              Un(e) assistant(e) pédagogique est rattaché(e) à une seule antenne : le sélecteur
              « tous les sites » ne lui est pas proposé, et une requête hors de son périmètre
              est refusée par l’API plutôt que filtrée en silence.
            </p>
          </div>
        </Carte>

        <Carte titre="Filtrage et exports" sansBordure>
          <div className="space-y-4 p-4 sm:p-6 text-sm text-codialis-gray">
            <p>
              Toutes les listes, tableaux de bord et exports peuvent être filtrés par
              site · à la manière d'un outil multi-boutiques.
            </p>
            <p className="rounded-xl bg-codialis-sand px-3 py-2 text-xs">
              Périmètre courant ·{' '}
              <span className="font-semibold text-codialis-navy">{perimetreLibelle}</span>
              {antenneUnique && (
                <span className="block pt-1 text-[11px]">
                  Votre compte est rattaché à une seule antenne : le sélecteur « tous
                  les sites » ne vous est pas proposé.
                </span>
              )}
            </p>
            {peutExporter ? (
              <div>
                <BoutonSecondaire onClick={exporter}>
                  <span className="inline-flex items-center gap-2">
                    <Icone nom="telecharger" className="h-4 w-4" />
                    Exporter les dossiers apprenants
                  </span>
                </BoutonSecondaire>
                {/* Nommer ce que l'export contient : la liste des rattachements
                    ci-contre dit ce que le périmètre borne, pas ce qui sort en
                    fichier. Le tableau de suivi partagé du CDC §4.7 ne porte que
                    les dossiers apprenants. */}
                <p className="pt-2 text-[11px]">
                  Tableau de suivi apprenants au format CSV, colonnes du référentiel
                  d’export. Entreprises, utilisateurs internes et sessions sont
                  rattachés à l’antenne mais ne figurent pas dans ce fichier.
                </p>
                <p className="pt-1 text-[11px]">
                  Le périmètre est appliqué par l’API, pas seulement ici : un site
                  hors périmètre est refusé, pas filtré en silence.
                </p>
              </div>
            ) : (
              <p className="rounded-xl bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
                L’export du suivi apprenants relève de l’écran « Export » du pôle
                administratif : votre profil n’y a pas accès.
              </p>
            )}
          </div>
        </Carte>
      </div>
    </div>
  );
}
